import React from 'react';
import PropTypes from 'prop-types';
import { ArrowTopRightOnSquareIcon, CalendarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import { formatEventDate } from '@/dashboard/lib/format-date';
import Button from '../../Button';
import Tooltip from '../../Tooltip';
import TicketTypeModalCheckbox from './TicketTypeMultiSelect/TicketTypeModalCheckbox';
import SelectedTicketsDisplay from './SelectedTicketsDisplay';
import TicketTypeMultiSelectList from './TicketTypeMultiSelect/TicketTypeMultiSelectList';

const PageMultiSelectPageContainer = ({
  page,
  isLast,
  onRemove,
  onVisit,
  showRemoveButton = true,
  includeTicketTypes = false,
  selectedTicketIds = [],
  onTicketTypeChange,
  isReadOnly = false,
  directTicketTypes = null,
}) => {
  const handleVisitClick = () => {
    if (onVisit) {
      onVisit(page._id);
    }
  };

  const handleRemoveClick = () => {
    if (onRemove) {
      onRemove(page);
    }
  };

  return (
    <div key={page._id}>
      <div
        className={`flex flex-col bg-white border-t-1 border-gray-200 py-2 ${isLast ? 'border-b-1' : ''}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-xs text-gray-400 capitalize">{page.type}</div>
            <div className="text-sm font-semibold mr-2">{page.name}</div>
            {page.type === 'checkout' && (
              <div className="text-gray-700">
                <span>{getProductPriceLabel(page.product)}</span>
              </div>
            )}
            {page.type === 'event' && page.eventDetails?.startDate && (
              <div className="text-gray-700">
                <div className="text-gray-700 text-sm">
                  <div className="flex items-center gap-x-1 justify-start">
                    <CalendarIcon className="h-4 w-4" />
                    <div className="flex flex-col font-medium">
                      {formatEventDate({
                        startDate: page.eventDetails.startDate,
                        endDate: page.eventDetails.endDate,
                        timezone: page.eventDetails.timezone,
                      }).join(' - ')}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-x-2">
            <Tooltip label="Visit page">
              <Button onClick={handleVisitClick} type="button" appearance="icon-button">
                <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-500" />
              </Button>
            </Tooltip>
            {showRemoveButton && !isReadOnly && (
              <Tooltip label="Remove page">
                <Button appearance="icon-button" onClick={handleRemoveClick}>
                  <XMarkIcon className="w-4 h-4" />
                </Button>
              </Tooltip>
            )}
          </div>
        </div>

        {includeTicketTypes && page.type === 'event' && (
          <>
            {directTicketTypes && directTicketTypes.length > 0 ? (
              <div className="mt-2">
                <TicketTypeMultiSelectList
                  ticketTypes={directTicketTypes}
                  selectedTicketTypes={new Map()}
                  interactive={false}
                  showCheckboxes={false}
                />
              </div>
            ) : (
              <>
                <SelectedTicketsDisplay pageId={page._id} selectedTicketIds={selectedTicketIds} />
                {!isReadOnly && (
                  <TicketTypeModalCheckbox
                    pageId={page._id}
                    onChange={onTicketTypeChange}
                    initialValue={selectedTicketIds}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

PageMultiSelectPageContainer.propTypes = {
  page: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    product: PropTypes.object,
    eventDetails: PropTypes.object,
  }).isRequired,
  isLast: PropTypes.bool.isRequired,
  onRemove: PropTypes.func,
  onVisit: PropTypes.func,
  showRemoveButton: PropTypes.bool,
  includeTicketTypes: PropTypes.bool,
  selectedTicketIds: PropTypes.arrayOf(PropTypes.string),
  onTicketTypeChange: PropTypes.func,
  isReadOnly: PropTypes.bool,
  directTicketTypes: PropTypes.arrayOf(PropTypes.object),
};

export default PageMultiSelectPageContainer;
